/* eslint no-console: 0 */
/*
 * The OpenTelemetry SDK half of browser tracing. Never import this module statically: it pulls in
 * zone.js (which patches Promise and timers the moment it evaluates) and every instrumentation.
 * ./telemetry.js requires it only when an OTLP endpoint was configured at build time, and a build
 * without one does not contain it. See docs/OTEL.md, "Nothing loads when tracing is off".
 */
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { ExportResultCode, W3CTraceContextPropagator } from '@opentelemetry/core';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { SpanKind, trace } from '@opentelemetry/api';
import { Resource } from '@opentelemetry/resources';
import {
  AlwaysOnSampler,
  BatchSpanProcessor,
  ParentBasedSampler,
  TraceIdRatioBasedSampler,
} from '@opentelemetry/sdk-trace-base';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { getWebAutoInstrumentations } from '@opentelemetry/auto-instrumentations-web';
import { createRoutes } from 'react-router';
import { matchPattern } from 'react-router/lib/PatternUtils';

import config from '../../config';

// mhub AWS-tagging `application` code for the Showroom family — see the otel-integration
// skill's tagging-alignment.md. Not a free-text product name.
const SERVICE_NAMESPACE = 'sr';
const SERVICE_NAME = 'mhub-book-web';

/**
 * Parses the `k=v,k2=v2` OTLP header syntax (percent-decoded values, e.g.
 * `authorization=Bearer%20<token>`). The browser SDK does not read
 * OTEL_EXPORTER_OTLP_HEADERS itself, so this is done by hand. A malformed
 * escape falls back to the raw value rather than throwing — one bad
 * character should not take tracing down.
 */
export const parseOtlpHeaders = (raw) => {
  const headers = {};
  if (!raw) return headers;

  raw.split(',').forEach((pair) => {
    const parts = pair.split('=');
    const key = parts[0];
    if (!key || parts.length < 2) return;
    const value = parts.slice(1).join('=').trim();
    try {
      headers[key.trim()] = decodeURIComponent(value);
    } catch (e) {
      headers[key.trim()] = value;
    }
  });
  return headers;
};

/**
 * The head sampler: ParentBased(TraceIdRatioBased(ratio)), i.e. `parentbased_traceidratio`.
 *
 * The browser starts almost every trace it is part of, so `ratio` is what decides how many of
 * them are kept. The decision travels in `traceparent`, and the Go tiers' default
 * ParentBased sampler honours it, so an unsampled click is unsampled end to end rather than
 * leaving orphaned backend halves. A ratio of 1 uses AlwaysOn: TraceIdRatioBased(1) still drops
 * the one trace id in 2^32 that lands on its upper bound.
 */
export const buildSampler = ratio => new ParentBasedSampler({
  root: ratio >= 1 ? new AlwaysOnSampler() : new TraceIdRatioBasedSampler(ratio),
});

// Both spellings of the request-URL attribute, because the two are a semconv rename mid-flight
// and this SDK line still emits the OLD one: instrumentation-fetch/-xml-http-request 0.57,
// document-load and user-interaction set "http.url", while 2.x and mhub-core-web use "url.full".
// Sanitising only `url.full` here silently does nothing — verified against a real
// headless-browser export, not read off the docs.
const URL_ATTRIBUTES = ['url.full', 'http.url'];

/**
 * Returns `raw` with its query string, fragment and any userinfo removed: origin and path only.
 *
 * Applied to every exported URL attribute, whatever the URL. A query string is caller data — the
 * purchaser search box, filters, tokens in a link — and once exported it sits in the trace
 * backend for every reader of it. The path stays, because it is what makes the span useful; span
 * names come from route templates (NavigationNameSpanProcessor, nameClientSpanFromRoute), not
 * from this attribute.
 */
export const stripQueryString = (raw) => {
  if (typeof raw !== 'string') return raw;
  try {
    const url = new URL(raw);
    url.search = '';
    url.hash = '';
    url.username = '';
    url.password = '';
    return url.toString();
  } catch (e) {
    // Relative or otherwise unparseable: still cut at the first `?` or `#`.
    return raw.split(/[?#]/)[0];
  }
};

// Query parameters whose presence makes a URL itself a bearer credential, replayable until it
// expires: CloudFront signed URLs (Signature, Key-Pair-Id, Expires, Policy) and S3 SigV4
// presigned URLs (X-Amz-Signature, X-Amz-Credential, X-Amz-Security-Token, …).
const SIGNED_URL_PARAM = /^(?:Signature|Key-Pair-Id|Expires|Policy|X-Amz-.+)$/i;
const URL_IN_TEXT = /https?:\/\/[^\s"'<>]+/gi;

/** Whether `raw` carries a signed-URL credential in its query string. */
export const isSignedUrl = (raw) => {
  try {
    let signed = false;
    new URL(raw).searchParams.forEach((value, key) => {
      if (SIGNED_URL_PARAM.test(key)) signed = true;
    });
    return signed;
  } catch (e) {
    return false;
  }
};

/**
 * Strips the query string from every signed URL embedded in free text, leaving the rest alone.
 *
 * The URL attributes are already stripped wholesale; this is for a signed URL that turns up
 * anywhere else — an error message, a status text, an attribute a later change adds. Those are
 * not URLs we know to look for, and a credential is the one thing that must not get through.
 */
export const redactSignedUrls = (text) => {
  if (typeof text !== 'string') return text;
  return text.replace(URL_IN_TEXT, match => (isSignedUrl(match) ? stripQueryString(match) : match));
};

const sanitizeAttributes = (attributes, isUrlAttribute) => {
  if (!attributes) return;
  Object.keys(attributes).forEach((key) => {
    const value = attributes[key];
    if (typeof value !== 'string') return;
    // eslint-disable-next-line no-param-reassign
    attributes[key] = isUrlAttribute(key) ? stripQueryString(value) : redactSignedUrls(value);
  });
};

// document-load records a `resourceFetch` span for every resource the page loaded, third-party
// scripts and fonts included. ignoreUrls cannot reach them — it is a fetch/XHR option — so they
// are dropped here instead.
const RESOURCE_FETCH = 'resourceFetch';

const pageOrigin = () => (typeof window !== 'undefined' && window.location ?
  window.location.origin : null);

const isThirdPartyResource = (span, isTracedUrl) => {
  if (span.name !== RESOURCE_FETCH) return false;
  const url = span.attributes['http.url'];
  if (typeof url !== 'string') return false;
  try {
    return new URL(url).origin !== pageOrigin() && !isTracedUrl(url);
  } catch (e) {
    return false;
  }
};

/**
 * Wraps an exporter so what leaves the browser is only what should:
 *
 * - every URL attribute loses its query string, fragment and userinfo (stripQueryString);
 * - a signed URL anywhere else in a string attribute, on the span or its events, loses its query
 *   string too (redactSignedUrls);
 * - document-load's `resourceFetch` spans for third-party resources are dropped.
 *
 * Applied at the exporter rather than through instrumentation-fetch's
 * applyCustomAttributesOnSpan: that hook receives the RequestInit (empty for
 * `fetch(url)`) and a FetchError that carries no URL, so a failed request would export the
 * URL anyway. Here every instrumentation and every outcome passes through one place — and it
 * runs after the span processors, which still see the full URL they resolve peers and route
 * names from.
 */
export const withExportSanitizer = (exporter, isTracedUrl = () => false) => ({
  export: (spans, resultCallback) => {
    const kept = spans.filter(span => !isThirdPartyResource(span, isTracedUrl));
    kept.forEach((span) => {
      sanitizeAttributes(span.attributes, key => URL_ATTRIBUTES.indexOf(key) !== -1);
      (span.events || []).forEach(event => sanitizeAttributes(event.attributes, () => false));
    });
    if (kept.length === 0) {
      resultCallback({ code: ExportResultCode.SUCCESS });
      return;
    }
    exporter.export(kept, resultCallback);
  },
  shutdown: () => exporter.shutdown(),
  forceFlush: () => (exporter.forceFlush ? exporter.forceFlush() : Promise.resolve()),
});

/** Returns the configured downstream whose origin owns `rawUrl`. */
export const peerServiceForUrl = (rawUrl, downstreams) => {
  try {
    const origin = new URL(rawUrl).origin;
    const downstream = downstreams.find(({ url }) => url && new URL(url).origin === origin);
    return downstream ? downstream.peerService : null;
  } catch (e) {
    return null;
  }
};

// The two MHub APIs this app calls. Read per call: bookApiURL is tenant-specific and arrives
// only after login.
const ownApis = () => [
  { url: config.get('apiURL'), peerService: 'mhub-api' },
  { url: config.get('bookApiURL'), peerService: 'mhub-book-api' },
];

// mhub-book-api and mhub-api return their SERVER span's route template in this header.
const ROUTE_HEADER = 'x-mhub-route';
// What a route template may look like. Anything else is ignored, so even a misbehaving server
// cannot put an arbitrary string into a span name.
const ROUTE_TEMPLATE = /^\/[A-Za-z0-9_\-/:.*]{0,200}$/;

/**
 * Names an API call's CLIENT span after the route the server matched: `GET /bookings/:id/`
 * rather than the instrumentation's `HTTP GET`, with the template as `url.template`.
 *
 * The browser cannot know the route itself — it only has the URL, and deciding which of its
 * segments are ids is exactly the guess the naming rule forbids. The server does know it, so
 * it hands it back. Only our own APIs are believed, and a 404 is left alone: mhub-book-api
 * collapses those onto their mount pattern AFTER the header is written.
 */
export const nameClientSpanFromRoute = (span, url, status, route) => {
  if (status === 404 || typeof route !== 'string' || !ROUTE_TEMPLATE.test(route)) return;
  if (!peerServiceForUrl(url, ownApis())) return;
  const method = span.attributes && span.attributes['http.method'];
  if (typeof method !== 'string') return;
  span.updateName(`${method.toUpperCase()} ${route}`);
  span.setAttribute('url.template', route);
};

const nameFetchSpan = (span, request, result) => {
  if (!result || !result.headers || typeof result.headers.get !== 'function') return;
  nameClientSpanFromRoute(span, result.url, result.status, result.headers.get(ROUTE_HEADER));
};

const nameXhrSpan = (span, xhr) => {
  nameClientSpanFromRoute(span, xhr.responseURL, xhr.status, xhr.getResponseHeader(ROUTE_HEADER));
};

/**
 * Adds the network identity SigNoz uses to draw browser -> API edges.
 *
 * Auto-instrumentation owns these CLIENT spans, so a processor is the one central hook that
 * covers fetch and XMLHttpRequest without decorating hundreds of call sites. The URL-to-service
 * mapping is resolved when the span ends because bookApiURL is tenant-specific and arrives only
 * after login.
 */
export class OutboundPeerSpanProcessor {
  /* eslint-disable class-methods-use-this */
  onStart() {}

  onEnd(span) {
    if (span.kind !== SpanKind.CLIENT) return;
    const rawUrl = URL_ATTRIBUTES.map(attribute => span.attributes[attribute])
      .find(value => typeof value === 'string');
    const peerService = peerServiceForUrl(rawUrl, ownApis());
    if (!peerService) return;

    const url = new URL(rawUrl);
    // ReadableSpan attributes are mutable until the following BatchSpanProcessor snapshots them.
    // This is also how withExportSanitizer above strips query strings at export.
    // eslint-disable-next-line no-param-reassign
    span.attributes['peer.service'] = peerService;
    // eslint-disable-next-line no-param-reassign
    span.attributes['server.address'] = url.hostname;
    if (url.port) {
      // URL.port is empty for default ports, which semconv says to omit.
      // eslint-disable-next-line no-param-reassign
      span.attributes['server.port'] = Number(url.port);
    }
  }

  shutdown() { return Promise.resolve(); }

  forceFlush() { return Promise.resolve(); }
  /* eslint-enable class-methods-use-this */
}

// instrumentation-user-interaction renames a click span to `Navigation: <url>` with the URL
// exactly as the app handed it to history.push — ids, query string and all.
const NAVIGATION_PREFIX = 'Navigation:';

// Full path templates of the app's registered routes; empty until setRouteTable() runs.
let routeTemplates = [];

const joinRoutePath = (parent, path) => {
  if (!path) return parent;
  if (path.charAt(0) === '/') return path;
  return parent.charAt(parent.length - 1) === '/' ? `${parent}${path}` : `${parent}/${path}`;
};

const collectTemplates = (routes, parent, out) => {
  routes.forEach((route) => {
    const template = joinRoutePath(parent, route.path);
    if (route.path) out.push(template);
    if (route.childRoutes) collectTemplates(route.childRoutes, template, out);
  });
  return out;
};

/**
 * Registers the react-router route table that navigation span names are resolved against.
 * A closed set of templates is what bounds the names: anything a caller types into the
 * address bar can only ever land on one of them, or on the bare fallback.
 */
export const setRouteTable = (routes) => {
  routeTemplates = collectTemplates(createRoutes(routes), '', [])
    .filter((template, i, all) => all.indexOf(template) === i);
};

// A literal segment beats a variable, a variable beats a splat.
const segmentRank = (segment) => {
  if (segment.indexOf('*') !== -1) return 0;
  if (segment.indexOf(':') !== -1) return 1;
  return 2;
};

// Most specific first: decided at the first segment where two templates differ, so the answer
// never depends on the order routes are declared in.
const bySpecificity = (a, b) => {
  const as = a.split('/');
  const bs = b.split('/');
  for (let i = 0; i < Math.max(as.length, bs.length); i += 1) {
    const diff = segmentRank(bs[i] || '*') - segmentRank(as[i] || '*');
    if (diff !== 0) return diff;
  }
  return 0;
};

/** Returns the registered template `url`'s path matches, or null when none does. */
export const routeTemplateForUrl = (url) => {
  let pathname;
  try {
    ({ pathname } = new URL(url, 'http://route.invalid'));
  } catch (e) {
    return null;
  }
  const matches = routeTemplates.filter((template) => {
    const match = matchPattern(template, pathname);
    return match && !match.remainingPathname;
  });
  return matches.length ? matches.sort(bySpecificity)[0] : null;
};

/**
 * Names navigation spans after the route template (`Navigation: /bookings/:id/`) instead of
 * the URL. An unmatched URL gets the bare `Navigation`, never the path the caller chose.
 */
export class NavigationNameSpanProcessor {
  /* eslint-disable class-methods-use-this */
  onStart() {}

  onEnd(span) {
    if (span.name.indexOf(NAVIGATION_PREFIX) !== 0) return;
    const template = routeTemplateForUrl(span.name.slice(NAVIGATION_PREFIX.length).trim());
    // The ended span is still the SDK's mutable Span object until the batcher snapshots it,
    // the same window OutboundPeerSpanProcessor uses for its attributes.
    // eslint-disable-next-line no-param-reassign
    span.name = template ? `${NAVIGATION_PREFIX} ${template}` : 'Navigation';
  }

  shutdown() { return Promise.resolve(); }

  forceFlush() { return Promise.resolve(); }
  /* eslint-enable class-methods-use-this */
}

/**
 * Stamps the current caller onto the trace's ROOT span at creation.
 *
 * A processor rather than per-call-site attributes because the browser's spans are created by
 * the auto-instrumentations (fetch, xhr, document-load) — there is no call site of ours to
 * decorate. onStart is the only hook that reaches them, and it runs before export, so the
 * attributes are on the span the exporter sends.
 *
 * Root span only, deliberately. SigNoz filters traces by their root span and then opens the
 * tree, so repeating identity on every child buys no query power and multiplies its storage by
 * the span count. That matters more here than it looks: a user id is high-cardinality, and it is
 * affordable as an attribute precisely because it appears once per trace.
 *
 * `getEndUser` is ./telemetry.js's accessor: the caller is recorded there at login whether or
 * not tracing is on, and read here per span.
 */
export class EndUserSpanProcessor {
  constructor(getEndUser) {
    this.getEndUser = getEndUser;
  }

  onStart(span, parentContext) {
    const endUser = this.getEndUser();
    if (!endUser) return;
    // A span with a parent already sits under a root that carries the caller. Reading the parent
    // from the context passed to onStart, rather than from the span, is what makes this work for
    // auto-instrumented spans: they are created from the active context, not by us.
    const parent = parentContext ? trace.getSpanContext(parentContext) : undefined;
    if (parent && parent.spanId) return;
    span.setAttributes(endUser);
  }

  /* eslint-disable class-methods-use-this */
  onEnd() {}

  shutdown() { return Promise.resolve(); }

  forceFlush() { return Promise.resolve(); }
  /* eslint-enable class-methods-use-this */
}

/**
 * Builds and registers the tracer provider and the instrumentations. Throws on bad
 * configuration — ./telemetry.js's initTelemetry() is what turns that into "run untraced" rather
 * than "do not start". Nothing is registered before the exporter is built, which is where a bad
 * endpoint throws.
 *
 * `propagateTraceHeaderCorsUrls` and `ignoreUrls` are ./telemetry.js's live arrays: both
 * instrumentations re-read them per request, so the tenant API registered after login is picked
 * up without re-registering anything.
 */
export const startTracing = ({
  endpoint,
  routes,
  // Every trace unless told otherwise — the same default ./telemetry.js's parseSamplerRatio has.
  samplerRatio = 1,
  propagateTraceHeaderCorsUrls,
  ignoreUrls,
  isTracedUrl,
  getEndUser,
}) => {
  const headers = parseOtlpHeaders(config.get('otelHeaders'));
  // An empty {} is still truthy, so spread it in conditionally: supplying
  // `headers` at all switches the exporter from sendBeacon to XHR, and only
  // sendBeacon survives page unload.
  const authenticated = Object.keys(headers).length > 0;

  const exporter = new OTLPTraceExporter(Object.assign(
    { url: `${endpoint}/v1/traces` },
    authenticated ? { headers } : {},
  ));

  // Account-scoped environment (equals the AWS `environment` tag), NOT
  // releaseStage — that is the application environment and names resources.
  // Falls back to it only until RESOURCE_ENVIRONMENT is plumbed through in CI.
  const resourceEnvironment = config.get('otelResourceEnvironment') || config.get('releaseStage');

  const provider = new WebTracerProvider({
    resource: new Resource({
      'service.name': SERVICE_NAME,
      'service.namespace': SERVICE_NAMESPACE,
      // config's `version` is "<pkg version> <build epoch ms>"; only the first token is
      // the version, and the tagging charset has no room for a space.
      'service.version': String(config.get('version') || '').split(' ')[0] || 'dev',
      'deployment.environment': resourceEnvironment,
      'deployment.environment.name': resourceEnvironment,
    }),
    sampler: buildSampler(samplerRatio),
    // The caller stamp runs first so every span carries it before the batcher sees it.
    spanProcessors: [
      new EndUserSpanProcessor(getEndUser),
      new OutboundPeerSpanProcessor(),
      new NavigationNameSpanProcessor(),
      new BatchSpanProcessor(withExportSanitizer(exporter, isTracedUrl)),
    ],
  });

  provider.register({
    contextManager: new ZoneContextManager(),
    propagator: new W3CTraceContextPropagator(),
  });

  if (routes) setRouteTable(routes);

  registerInstrumentations({
    instrumentations: [
      getWebAutoInstrumentations({
        '@opentelemetry/instrumentation-fetch': {
          propagateTraceHeaderCorsUrls,
          ignoreUrls,
          applyCustomAttributesOnSpan: nameFetchSpan,
        },
        '@opentelemetry/instrumentation-xml-http-request': {
          propagateTraceHeaderCorsUrls,
          ignoreUrls,
          applyCustomAttributesOnSpan: nameXhrSpan,
        },
      }),
    ],
  });

  console.info('[telemetry] OpenTelemetry tracing initialised', endpoint,
    `sampler ratio ${samplerRatio}`);
};



// WEBPACK FOOTER //
// ./src/lib/telemetrySdk.js